/* =====================================================================================
   Studio seed data: marketing assets and the MLR review process.

   The claim-level ranking below mirrors studio.claim_level_rank() in the database. The
   gate compares an asset's declared claim level against the regulatory status of every
   product it promotes, in every territory it is destined for.
   ===================================================================================== */

import { demo } from './demo';


export type ClaimLevel =
  | 'corporate' | 'ruo' | 'technical'
  | 'clinical_performance' | 'clinical_utility' | 'comparative' | 'economic';

export const CLAIM_RANK: Record<ClaimLevel, number> = {
  corporate: 0, ruo: 1, technical: 2,
  clinical_performance: 3, clinical_utility: 4, economic: 4, comparative: 5,
};

export const CLAIM_LABEL: Record<ClaimLevel, string> = {
  corporate: 'Corporate',
  ruo: 'Research use only',
  technical: 'Analytical performance',
  clinical_performance: 'Clinical performance',
  clinical_utility: 'Clinical utility',
  comparative: 'Comparative',
  economic: 'Health economic',
};

/** What each regulatory status permits. Mirrors studio.claim_gate_rules. */
/* The seed's gate table, used only when there is no database (a live tenant's gate verdict
   arrives on the version from studio.check_claim_gate). Regulatory statuses here are the first
   tenant's diagnostics vocabulary and the neutral starter (0087). No disclaimer text: nothing
   read it, and the RUO sentence it carried was shipping in every tenant's bundle. */
export const GATE_RULES: Record<string, { max: ClaimLevel }> = {
  ruo: { max: 'technical' },
  investigational: { max: 'technical' },
  approved: { max: 'clinical_utility' },
  ldt: { max: 'clinical_performance' },
  ce_ivd: { max: 'clinical_utility' },
  ukca: { max: 'clinical_utility' },
  unknown: { max: 'corporate' },
};

export interface Product {
  id: string;
  name: string;
  /** Regulatory status per ISO3 territory. */
  status: Record<string, string>;
}

export const PRODUCTS: Product[] = demo<Product>([
  { id: 'cfdna', name: 'Scira cfDNA', status: { GBR: 'ruo', ITA: 'ruo', USA: 'ruo', DEU: 'ruo' } },
  { id: 'hla', name: 'Scira HLA', status: { GBR: 'ruo', ITA: 'ruo', ESP: 'ruo' } },
  { id: 'hct', name: 'Scira HCT', status: { GBR: 'ruo', ITA: 'ruo' } },
]);

export interface Claim {
  code: string;
  text: string;
  level: ClaimLevel;
  status: 'draft' | 'in_review' | 'approved' | 'rejected' | 'retired';
  references: { citation: string; strength: 'primary' | 'supporting' | 'contextual' }[];
}

export const CLAIMS: Claim[] = demo<Claim>([
  {
    code: 'CL-001',
    text: 'Nanopore sequencing enables a same-day donor-derived cell-free DNA result.',
    level: 'technical', status: 'approved',
    references: [{ citation: 'Internal analytical validation report SC-AV-004, 2026', strength: 'primary' }],
  },
  {
    code: 'CL-002',
    text: 'Scira cfDNA is for research use only and is not for use in diagnostic procedures.',
    level: 'ruo', status: 'approved',
    references: [{ citation: 'Product labelling specification SC-LBL-001', strength: 'primary' }],
  },
  {
    code: 'CL-007',
    text: 'Earlier detection of rejection improves graft survival.',
    level: 'clinical_utility', status: 'in_review',
    references: [
      { citation: 'Published literature review, pending internal substantiation', strength: 'contextual' },
    ],
  },
  {
    code: 'CL-009',
    text: 'Analytical limit of detection of 0.10% donor fraction.',
    level: 'technical', status: 'approved',
    references: [{ citation: 'Internal analytical validation report SC-AV-004, 2026', strength: 'primary' }],
  },
]);

export interface Annotation {
  id: string;
  page: number;
  x: number;          // normalised 0..1 against the page box
  y: number;
  author: string;
  discipline: 'medical' | 'legal' | 'regulatory' | 'commercial' | 'brand';
  body: string;
  severity: 'blocker' | 'major' | 'minor' | 'comment';
  category?: string;
  status: 'open' | 'addressed' | 'accepted';
  createdAt: string;
  replies?: { author: string; body: string; createdAt: string }[];
  carriedForward?: boolean;
}

export interface AssetVersion {
  n: number;
  label: string;
  status: 'draft' | 'submitted' | 'in_review' | 'changes_required' | 'approved' | 'superseded';
  claimLevel: ClaimLevel;
  claims: string[];
  pages: number;
  submittedAt?: string;
  decidedAt?: string;
  locked: boolean;
  changeSummary?: string;
  /* The claim-gate verdict for this version as the DATABASE computed it (studio.check_claim_gate
     via studio.v_assets). Present for a live tenant; absent for the seed, where checkClaimGate
     below evaluates against the compiled product list. When present it wins, because it is the
     same function that will refuse the submission, and the screen must agree with the refusal. */
  gate?: { territory: string; product: string; status: string; maxAllowed: ClaimLevel; message: string }[];
  /** The version's row id, for a live tenant. Absent for the seed. */
  dbId?: string;
  annotations: Annotation[];
  reviewers: {
    name: string;
    discipline: 'medical' | 'legal' | 'regulatory';
    required: boolean;
    certifier?: boolean;
    decision?: 'approved' | 'approved_with_comments' | 'changes_required' | 'rejected';
    decidedAt?: string;
    note?: string;
  }[];
}

export interface Asset {
  id: string;
  jobCode: string;
  title: string;
  type: string;
  products: string[];
  territories: string[];
  audience: string[];
  owner: string;
  status: 'draft' | 'in_review' | 'changes_required' | 'approved' | 'effective' | 'expired' | 'withdrawn';
  expiresOn?: string;
  versions: AssetVersion[];
}

export const ASSETS: Asset[] = demo<Asset>([
  {
    id: 'a1', jobCode: 'SCI-2026-0012',
    title: 'Scira cfDNA — congress leave-behind',
    type: 'one_pager',
    products: ['cfdna'], territories: ['GBR', 'ITA'], audience: ['hcp'],
    owner: 'Dr Lawrence', status: 'in_review',
    versions: [
      {
        n: 2, label: 'v2.0', status: 'in_review', claimLevel: 'technical',
        claims: ['CL-001', 'CL-002', 'CL-009'], pages: 2,
        submittedAt: '2026-07-28', locked: false,
        changeSummary: 'Removed the graft survival claim raised as a blocker on v1. Added the RUO statement to the footer of both pages.',
        annotations: [
          {
            id: 'an1', page: 1, x: 0.52, y: 0.28,
            author: 'Medical reviewer', discipline: 'medical',
            body: 'The turnaround claim needs the qualifier "from sample receipt", otherwise it reads as time from blood draw, which we cannot support.',
            severity: 'major', category: 'claim_unsubstantiated', status: 'open',
            createdAt: '2026-07-30',
          },
          {
            id: 'an2', page: 1, x: 0.30, y: 0.62,
            author: 'Regulatory reviewer', discipline: 'regulatory',
            body: 'RUO statement present and correctly placed. No objection.',
            severity: 'comment', status: 'accepted', createdAt: '2026-07-30',
          },
          {
            id: 'an3', page: 2, x: 0.66, y: 0.44,
            author: 'Legal reviewer', discipline: 'legal',
            body: 'The comparison table names AlloSure. A named comparison is a comparative claim and needs head-to-head data we do not have. Remove the column or remove the name.',
            severity: 'blocker', category: 'claim_exceeds_status', status: 'open',
            createdAt: '2026-07-31',
            replies: [{ author: 'Dr Lawrence', body: 'Agreed. Removing the named column in v3.', createdAt: '2026-08-01' }],
          },
        ],
        reviewers: [
          { name: 'Medical reviewer', discipline: 'medical', required: true, decision: 'changes_required', decidedAt: '2026-07-30', note: 'One qualifier needed.' },
          { name: 'Legal reviewer', discipline: 'legal', required: true, decision: 'changes_required', decidedAt: '2026-07-31' },
          { name: 'Regulatory reviewer', discipline: 'regulatory', required: true, certifier: true },
        ],
      },
      {
        n: 1, label: 'v1.0', status: 'superseded', claimLevel: 'clinical_utility',
        claims: ['CL-001', 'CL-007'], pages: 2,
        submittedAt: '2026-07-14', decidedAt: '2026-07-16', locked: false,
        changeSummary: 'Initial draft.',
        annotations: [
          {
            id: 'an0', page: 1, x: 0.4, y: 0.5,
            author: 'Regulatory reviewer', discipline: 'regulatory',
            body: 'REJECTED AT GATE. Declared claim level "clinical utility" exceeds RUO status in both GBR and ITA. An RUO product cannot carry a claim that it improves patient outcomes.',
            severity: 'blocker', category: 'claim_exceeds_status', status: 'addressed',
            createdAt: '2026-07-16',
          },
        ],
        reviewers: [
          { name: 'Regulatory reviewer', discipline: 'regulatory', required: true, certifier: true, decision: 'rejected', decidedAt: '2026-07-16', note: 'Blocked by the claim gate before human review.' },
        ],
      },
    ],
  },
  {
    id: 'a2', jobCode: 'SCI-2026-0008',
    title: 'Corporate overview deck',
    type: 'slide_deck',
    products: [], territories: ['GBR', 'ITA', 'USA', 'ESP'], audience: ['investor', 'hcp'],
    owner: 'Dr Lawrence', status: 'effective', expiresOn: '2028-06-14',
    versions: [
      {
        n: 3, label: 'v3.0', status: 'approved', claimLevel: 'corporate',
        claims: [], pages: 24,
        submittedAt: '2026-06-02', decidedAt: '2026-06-14', locked: true,
        changeSummary: 'Updated company number and registered office.',
        annotations: [],
        reviewers: [
          { name: 'Medical reviewer', discipline: 'medical', required: true, decision: 'approved', decidedAt: '2026-06-10' },
          { name: 'Legal reviewer', discipline: 'legal', required: true, decision: 'approved', decidedAt: '2026-06-12' },
          { name: 'Regulatory reviewer', discipline: 'regulatory', required: true, certifier: true, decision: 'approved', decidedAt: '2026-06-14' },
        ],
      },
    ],
  },
  {
    id: 'a3', jobCode: 'SCI-2026-0019',
    title: 'Scira HLA — laboratory data sheet',
    type: 'leaflet',
    products: ['hla'], territories: ['GBR', 'ESP'], audience: ['hcp'],
    owner: 'Dr Lawrence', status: 'draft',
    versions: [
      {
        n: 1, label: 'v1.0 draft', status: 'draft', claimLevel: 'clinical_performance',
        claims: ['CL-009'], pages: 4, locked: false,
        annotations: [], reviewers: [],
      },
    ],
  },
]);

/** Mirrors studio.check_claim_gate(). Returns violations; empty means the gate passes. */
export function checkClaimGate(asset: Asset, version: AssetVersion) {
  const out: { territory: string; product: string; status: string; maxAllowed: ClaimLevel; message: string }[] = [];
  if (version.gate) return version.gate;      // the database has already judged this version
  if (!asset.products.length) return out;   // corporate material promotes no product
  for (const terr of asset.territories) {
    for (const pid of asset.products) {
      const p = PRODUCTS.find((x) => x.id === pid);
      if (!p) continue;
      const status = p.status[terr] ?? 'unknown';
      const rule = GATE_RULES[status] ?? GATE_RULES.unknown;
      if (CLAIM_RANK[version.claimLevel] > CLAIM_RANK[rule.max]) {
        out.push({
          territory: terr, product: p.name, status, maxAllowed: rule.max,
          message:
            `Asset claims at "${CLAIM_LABEL[version.claimLevel]}" but ${p.name} is ${status.toUpperCase()} ` +
            `in ${terr}, which permits at most "${CLAIM_LABEL[rule.max]}".`,
        });
      }
    }
  }
  return out;
}
